import { FC, HTMLAttributes, ReactElement, ReactChild, memo, useCallback, useState } from 'react';
import cn from 'classnames';

import Section from '@Components/Section';
import CardInView from '@Components/CardInView';
import Gallery, { ProgressOptions } from '@UI/Gallery';
import NavArrows from '@UI/NavArrows';
import ProgressBar from '@UI/ProgressBar';
import { ProductData } from '@Types/Product';
import styles from './CrossSaleSection.module.css';

export interface RenderItem {
  product: ProductData;
  position: number;
}

export interface CrossSaleSectionProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  className?: string;
  cnGallery?: string;
  title?: ReactChild;
  analyticsTitle: string;
  tabs?: ReactElement;
  text?: string;
  products: ProductData[];
  offset?: number;
  renderItem?: (props: RenderItem) => ReactElement;
}

const CrossSaleSection: FC<CrossSaleSectionProps> = (props) => {
  const {
    className,
    cnGallery,
    title,
    analyticsTitle,
    products,
    text,
    tabs,
    offset = 0,
    renderItem,
    ...restProps
  } = props;
  const [slide, setSlide] = useState(0);
  const [track, setTrack] = useState<ProgressOptions>(null);

  const normalizeSlide = useCallback(
    (value: number) => {
      if (value < 0) return 0;
      if (value > products.length) return products.length;

      return value;
    },
    [products.length],
  );

  const handleChangeCurrent = useCallback(({ current }) => {
    setSlide(current);
  }, []);

  const handleChangeProgress = useCallback((opts: ProgressOptions) => {
    setTrack(opts);
  }, []);

  const handlePrev = useCallback(() => {
    setSlide((prev) => normalizeSlide(prev - 1));
  }, [normalizeSlide]);

  const handleNext = useCallback(() => {
    if (track.finished) return;

    setSlide((prev) => normalizeSlide(prev + 1));
  }, [normalizeSlide, track]);

  return (
    <Section
      {...restProps}
      className={cn(styles.section, className)}
      title={title}
      arrows={
        <NavArrows
          className={cn(styles.arrows, { [styles.visible]: track?.width < 100 })}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      }
    >
      <div className={styles.container}>{text && <div className={styles.text}>{text}</div>}</div>
      {tabs && <div className={styles.tabs}>{tabs}</div>}
      <div className={styles.wrapperGallery}>
        <Gallery
          className={cn(styles.gallery, cnGallery)}
          key={products.length}
          slideIndex={slide}
          onChangeCurrent={handleChangeCurrent}
          onChangeProgress={handleChangeProgress}
        >
          {products.map((product, index) => {
            const elem = renderItem({
              product,
              position: index,
            });

            return (
              <div key={product.id}>
                <CardInView
                  listTitle={analyticsTitle}
                  card={product}
                  position={offset + index}
                  cardType='product'
                  data-testid='product-card'
                  id={product.id}
                >
                  {elem}
                </CardInView>
              </div>
            );
          })}
        </Gallery>
      </div>

      {track?.width < 100 && (
        <div className={styles.wrapperProgress}>
          <ProgressBar className={styles.progressBar} track={track} />
        </div>
      )}
    </Section>
  );
};

export default memo(CrossSaleSection);
